import React, { useMemo, type ComponentType, type ReactNode } from 'react';
import type { RowClickedEvent } from 'ag-grid-community';
import AgGridWrapper from '@/app/common/components/ag-grid/AgGridWrapper';
import AgGridSkeleton from '@/app/common/components/skeleton/AgGridSkeleton';
import { executionsColumnConfig } from '../../config/executionsColumnConfig';
import type { PromptHistoryItem } from '../../types';

type AgGridWrapperProps = {
  table_name?: string;
  height?: string;
  columns: unknown[];
  data: unknown[];
  total?: number;
  settings?: Record<string, unknown>;
  otherOptions?: Record<string, unknown>;
};
const TypedAgGridWrapper = AgGridWrapper as ComponentType<AgGridWrapperProps>;

export function ExecutionsTable({
  rows,
  loading,
  emptyMessage,
  selectedId,
  onView,
  onReextract,
}: {
  rows: PromptHistoryItem[];
  loading?: boolean;
  emptyMessage: ReactNode;
  selectedId?: string | null;
  onView: (executionId: string) => void;
  onReextract: (promptId: string) => void;
}) {
  const columns = useMemo(
    () => executionsColumnConfig({ onView, onReextract }),
    [onView, onReextract],
  );

  const otherOptions = useMemo(
    () => ({
      onRowClicked: (event: RowClickedEvent<PromptHistoryItem>) => {
        const target = event.event?.target;
        if (target instanceof Element && target.closest('button, a, .dropdown-menu, .dropdown')) {
          return;
        }
        if (event.data) onView(event.data.id);
      },
      getRowStyle: (params: { data?: PromptHistoryItem }) =>
        params.data && params.data.id === selectedId
          ? { background: 'rgba(99, 102, 241, 0.08)' }
          : undefined,
    }),
    [onView, selectedId],
  );

  if (loading) {
    return <AgGridSkeleton height="640px" />;
  }

  if (!rows.length) {
    return <div className="py-6 text-center small text-body-tertiary">{emptyMessage}</div>;
  }

  return (
    <TypedAgGridWrapper
      table_name="tracker_ai_executions"
      height="640px"
      columns={columns}
      data={rows}
      total={rows.length}
      settings={{ pagination: true, fullscreen: false, sideBar: false, export: { enabled: false } }}
      otherOptions={otherOptions}
    />
  );
}
